import { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { API_BASE } from '../utils/apiBase';
import {
  registerActive,
  syncActiveTokensFromCanonical,
  activateCanonical,
  getAccounts,
  getAccount,
  getActiveId,
  removeAccount,
  clearRegistry,
  listAccountsForUi,
  accountIdOf,
  profileFromUser,
  upsertAccount,
  expiryFromAuth,
} from '../services/accountRegistry';
import { samePlaceAfterAccountChange } from '../utils/returnTo';
import { getStaySignedIn } from '../services/staySignedIn';
import { inviteRefCode } from '../utils/inviteRef';
import { refreshSession } from '../services/sessionRefresh';

const AuthContext = createContext(null);

const API_URL = API_BASE;
const TOKEN_KEY = 'accessToken';
const REFRESH_TOKEN_KEY = 'refreshToken';
const TOKEN_EXPIRY_KEY = 'tokenExpiry';

// Token refresh interval (refresh 5 minutes before expiry)
const REFRESH_BUFFER_MS = 5 * 60 * 1000;
// How long to wait before trying a refresh again when the server could not be
// reached (a deploy's API restart, a dropped connection). Short enough that the
// session is healthy again moments after the API is back. A 429 from the auth
// throttle names its own wait (Retry-After), which wins when it is longer.
const REFRESH_RETRY_MS = 20 * 1000;

// The stored access token is already dead (not merely inside the refresh buffer).
const tokenExpired = () => {
  const expiryTime = localStorage.getItem(TOKEN_EXPIRY_KEY);
  return !!expiryTime && parseInt(expiryTime, 10) <= Date.now();
};

// A server that accepts a connection but never answers must also be retried.
async function fetchSession(url, options) {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), 15000);
  try {
    return await fetch(url, { ...options, signal: controller.signal });
  } finally {
    clearTimeout(timer);
  }
}

export function AuthProvider({ children }) {
  const refreshInFlight = useRef(null);
  // Set by a refresh the server asked us to hold off on (Retry-After); 0 otherwise.
  const retryAfterRef = useRef(0);
  const retryDelay = useCallback(() => Math.max(REFRESH_RETRY_MS, retryAfterRef.current || 0), []);
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY));
  const [isLoading, setIsLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  // Multi-account: the token-free list of every signed-in account (for the switcher).
  const [savedAccounts, setSavedAccounts] = useState(() => listAccountsForUi());
  const [activeAccountId, setActiveAccountId] = useState(() => getActiveId());

  const refreshAccounts = useCallback(() => {
    setSavedAccounts(listAccountsForUi());
    setActiveAccountId(getActiveId());
  }, []);

  // Snapshot the just-authenticated user as the active account, then republish the list.
  const registerActiveAccount = useCallback((u) => {
    registerActive(u);
    setSavedAccounts(listAccountsForUi());
    setActiveAccountId(getActiveId());
  }, []);

  // Store tokens in localStorage
  const storeTokens = useCallback((accessToken, refreshToken, expiresIn, expiresAt) => {
    // Guard: writing undefined here stores the string "undefined", which passes
    // every "is there a token?" check and then 401s forever.
    if (!accessToken) return;
    localStorage.setItem(TOKEN_KEY, accessToken);
    if (refreshToken) {
      localStorage.setItem(REFRESH_TOKEN_KEY, refreshToken);
    }
    const expiry = expiryFromAuth({ expiresIn, expiresAt });
    if (expiry) localStorage.setItem(TOKEN_EXPIRY_KEY, expiry);
    else localStorage.removeItem(TOKEN_EXPIRY_KEY);
    setToken(accessToken);
  }, []);

  // Clear all stored tokens
  const clearTokens = useCallback(() => {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(REFRESH_TOKEN_KEY);
    localStorage.removeItem(TOKEN_EXPIRY_KEY);
    localStorage.removeItem('pr.activePage'); // drop any "acting as page" state
    setToken(null);
    setUser(null);
    setIsAuthenticated(false);
  }, []);

  // Refresh the access token. ONE refresh for the whole app: `refreshSession` is the
  // same in-flight promise the axios interceptor awaits, so the boot refresh and a
  // 401'd API call in the same second present the single-use token ONCE. Two at a
  // time used to earn the loser a 500 (the row the winner had just rotated) and
  // spend two slots of nginx's auth throttle.
  const performRefresh = useCallback(async () => {
    const result = await refreshSession();
    if (result.accessToken) {
      retryAfterRef.current = 0;
      setToken(result.accessToken);
      return result.accessToken;
    }
    if (result.rejected) {
      // Only the server REJECTING the token means the session is over.
      clearTokens();
      return null;
    }
    // Could not go through: offline, the API restarting mid-deploy, a 5xx, the auth
    // throttle's 429. The token is untouched by definition, so keep it and come
    // back to it — after the wait the server named, when it named one.
    retryAfterRef.current = result.retryAfterMs || 0;
    console.warn(`Token refresh could not go through (${result.status || 'no response'}); keeping the session.`, result.error || '');
    return null;
  }, [clearTokens]);

  // Initialization and the expiry timer can request refresh at the same time.
  const refreshAccessToken = useCallback(() => {
    if (!refreshInFlight.current) {
      refreshInFlight.current = performRefresh().finally(() => {
        refreshInFlight.current = null;
      });
    }
    return refreshInFlight.current;
  }, [performRefresh]);

  // Check if token needs refresh
  const checkTokenExpiry = useCallback(() => {
    const expiryTime = localStorage.getItem(TOKEN_EXPIRY_KEY);
    if (!expiryTime) return false;

    const timeUntilExpiry = parseInt(expiryTime, 10) - Date.now();
    return timeUntilExpiry < REFRESH_BUFFER_MS;
  }, []);

  // Login function
  // Apply a session handed back by a server flow that already authenticated the
  // user (e.g. accountless "make an offer" mints a stub account + tokens). Mirrors
  // login()'s state changes without a password round-trip.
  const applyGuestSession = useCallback((auth) => {
    if (!auth?.accessToken) return;
    storeTokens(auth.accessToken, auth.refreshToken, auth.expiresIn, auth.expiresAt);
    if (auth.user) { setUser(auth.user); registerActiveAccount(auth.user); }
    setIsAuthenticated(true);
  }, [storeTokens, registerActiveAccount]);

  const login = useCallback(async (email, password) => {
    setIsLoading(true);
    try {
      const response = await fetch(`${API_URL}/auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email, password, rememberMe: getStaySignedIn() }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || 'Login failed');
      }

      // The API answers { success, data: { accessToken, … } }. Reading the
      // envelope instead of its payload yields undefined for every field, and
      // storeTokens then writes the STRING "undefined" into localStorage — a
      // session that looks present and 401s on every call.
      const body = await response.json();
      const data = body.data || body;
      storeTokens(data.accessToken, data.refreshToken, data.expiresIn, data.expiresAt);
      setUser(data.user);
      setIsAuthenticated(true);
      registerActiveAccount(data.user);
      return { success: true, user: data.user };
    } catch (error) {
      return { success: false, error: error.message };
    } finally {
      setIsLoading(false);
    }
  }, [storeTokens, registerActiveAccount]);

  // Register function
  const register = useCallback(async (userData) => {
    setIsLoading(true);
    try {
      const response = await fetch(`${API_URL}/auth/register`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ rememberMe: getStaySignedIn(), ref: inviteRefCode(), ...userData }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || 'Registration failed');
      }

      const regBody = await response.json();
      const data = regBody.data || regBody;
      storeTokens(data.accessToken, data.refreshToken, data.expiresIn, data.expiresAt);
      setUser(data.user);
      setIsAuthenticated(true);
      registerActiveAccount(data.user);
      return { success: true, user: data.user };
    } catch (error) {
      return { success: false, error: error.message };
    } finally {
      setIsLoading(false);
    }
  }, [storeTokens, registerActiveAccount]);

  // Logout function
  const logout = useCallback(async () => {
    try {
      const currentToken = localStorage.getItem(TOKEN_KEY);
      if (currentToken) {
        await fetch(`${API_URL}/auth/logout`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${currentToken}`,
            'Content-Type': 'application/json',
          },
        });
      }
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      // Tear down the SignalR chat connection so the next user doesn't
      // inherit the previous user's WebSocket.
      try {
        const { disconnectMessagesRealtime } = await import('../services/messagesRealtime');
        await disconnectMessagesRealtime();
      } catch {}
      clearTokens();
      // Full logout wipes every saved account too.
      clearRegistry();
      refreshAccounts();
    }
  }, [clearTokens, refreshAccounts]);

  // Fetch current user data
  const fetchCurrentUser = useCallback(async (accessToken) => {
    try {
      const request = (value) => fetchSession(`${API_URL}/users/me`, {
        headers: { Authorization: `Bearer ${value}` },
      });
      let response = await request(accessToken);
      if (response.status === 401) {
        const fresh = await refreshAccessToken();
        if (!fresh) return null;
        accessToken = fresh;
        response = await request(fresh);
      }
      if (localStorage.getItem(TOKEN_KEY) !== accessToken) return null;

      if (!response.ok) {
        throw new Error('Failed to fetch user');
      }

      const userData = await response.json();
      const user = userData.data || userData;
      setUser(user);
      setIsAuthenticated(true);
      // Keep the active account's profile + tokens fresh in the registry.
      registerActiveAccount(user);
      return userData;
    } catch (error) {
      // A failed profile request is not proof that the session was revoked.
      console.warn('Session restore pending; keeping stored tokens.', error);
      return null;
    }
  }, [refreshAccessToken, registerActiveAccount]);

  // Keep the auth gate pending during a deploy, and restore it automatically
  // once the API answers. Only a rejected refresh ends the saved session.
  useEffect(() => {
    let cancelled = false;
    let timer;
    const initializeAuth = async () => {
      let storedToken = localStorage.getItem(TOKEN_KEY);
      if (!storedToken) {
        if (!cancelled) setIsLoading(false);
        return;
      }
      if (checkTokenExpiry()) {
        const fresh = await refreshAccessToken();
        if (cancelled) return;
        if (fresh) {
          storedToken = fresh;
        } else if (!localStorage.getItem(TOKEN_KEY)) {
          // Rejected: the session is over and the gate opens signed out.
          setIsLoading(false);
          return;
        } else if (tokenExpired()) {
          // The refresh could not go through and the stored access token is already
          // dead. Spending a /users/me on it is a guaranteed 401 and a SECOND refresh —
          // four requests a lap, which is what kept a throttled page throttled. Wait.
          timer = setTimeout(initializeAuth, retryDelay());
          return;
        }
      }
      const restored = await fetchCurrentUser(storedToken);
      if (cancelled) return;
      if (restored || !localStorage.getItem(TOKEN_KEY)) {
        setIsLoading(false);
      } else {
        timer = setTimeout(initializeAuth, retryDelay());
      }
    };
    initializeAuth();
    return () => { cancelled = true; clearTimeout(timer); };
  }, [checkTokenExpiry, refreshAccessToken, fetchCurrentUser, retryDelay]);

  // Set up automatic token refresh. A success changes `token`, which re-runs this
  // and arms the next one; a refresh that could not reach the server leaves the
  // tokens in place and is retried on a short timer, so a restarting API costs a
  // retry rather than the session.
  useEffect(() => {
    if (!token || !isAuthenticated) return;

    const expiryTime = localStorage.getItem(TOKEN_EXPIRY_KEY);
    if (!expiryTime) return;

    let timer = null;
    const attempt = async () => {
      const fresh = await refreshAccessToken();
      if (!fresh && localStorage.getItem(TOKEN_KEY)) {
        timer = setTimeout(attempt, retryDelay());
      }
    };

    const timeUntilRefresh = parseInt(expiryTime, 10) - Date.now() - REFRESH_BUFFER_MS;
    if (timeUntilRefresh <= 0) attempt();
    else timer = setTimeout(attempt, timeUntilRefresh);

    return () => { if (timer) clearTimeout(timer); };
  }, [token, isAuthenticated, refreshAccessToken, retryDelay]);

  // ──── Multi-account ────
  // Tear down the current user's realtime socket so the next account doesn't
  // inherit it. Shared by switch / add / sign-out.
  const disconnectRealtime = useCallback(async () => {
    try {
      const { disconnectMessagesRealtime } = await import('../services/messagesRealtime');
      await disconnectMessagesRealtime();
    } catch {}
  }, []);

  // Switch to another already-signed-in account. Copies its tokens into the
  // canonical keys and reloads so every context re-inits cleanly for that user.
  const switchAccount = useCallback(async (id) => {
    if (!id || id === getActiveId()) return { success: false };
    const target = getAccount(id);
    if (!target) return { success: false, error: 'Account not found' };
    syncActiveTokensFromCanonical(); // save the current account's latest tokens first
    activateCanonical(target);
    await disconnectRealtime();
    // Stay where you were — becoming a different person shouldn't also move you.
    window.location.assign(samePlaceAfterAccountChange());
    return { success: true };
  }, [disconnectRealtime]);

  // Sign in to ANOTHER account without dropping the current one. Runs the normal
  // login, snapshots the current account first, then activates + reloads.
  const addAccount = useCallback(async (email, password) => {
    try {
      const response = await fetch(`${API_URL}/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password }),
      });
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.message || 'Login failed');
      }
      // The API answers { success, data: { user, accessToken, … } }. Reading the
      // envelope left every field undefined, so accountIdOf(undefined) came back
      // null and this threw "Login failed" — telling the user their password was
      // wrong on a login the server had just ACCEPTED. That was the bug behind
      // "my password is correct but it won't log me in".
      const body = await response.json();
      const data = body.data || body;
      const newId = accountIdOf(data.user);
      if (!newId) throw new Error('Login failed');

      syncActiveTokensFromCanonical(); // preserve the current account before we swap
      if (newId === getActiveId()) {
        return { success: true, alreadyActive: true }; // same account — nothing to add
      }
      const tokens = {
        accessToken: data.accessToken,
        refreshToken: data.refreshToken,
        tokenExpiry: expiryFromAuth(data),
      };
      upsertAccount(newId, tokens, profileFromUser(data.user));
      activateCanonical({ id: newId, ...tokens });
      await disconnectRealtime();
      window.location.assign(samePlaceAfterAccountChange());
      return { success: true };
    } catch (error) {
      return { success: false, error: error.message };
    }
  }, [disconnectRealtime]);

  // Sign out of the current account. If another account remains, switch to it;
  // otherwise fall through (returns false) so the caller does a full logout.
  const signOutCurrent = useCallback(async () => {
    const activeId = getActiveId();
    try {
      const currentToken = localStorage.getItem(TOKEN_KEY);
      const refreshToken = localStorage.getItem(REFRESH_TOKEN_KEY);
      if (currentToken) {
        await fetch(`${API_URL}/auth/logout`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${currentToken}`, 'Content-Type': 'application/json' },
          body: JSON.stringify({ refreshToken }),
        });
      }
    } catch {}
    if (activeId) removeAccount(activeId);
    const remaining = getAccounts();
    await disconnectRealtime();
    if (remaining.length > 0) {
      activateCanonical(remaining[0]);
      window.location.assign('/');
      return true;
    }
    // No accounts left — hand back to the caller's full logout.
    clearRegistry();
    return false;
  }, [disconnectRealtime]);

  // Get auth header helper
  const getAuthHeader = useCallback(() => {
    const currentToken = localStorage.getItem(TOKEN_KEY);
    return currentToken ? { Authorization: `Bearer ${currentToken}` } : {};
  }, []);

  /**
   * Re-read /users/me into context. Needed whenever something outside the auth
   * flow changes the account record — verifying a phone number, for one — so the
   * UI doesn't keep rendering the stale copy from login.
   */
  const refreshUser = useCallback(async () => {
    const accessToken = token || localStorage.getItem(TOKEN_KEY);
    if (!accessToken) return null;
    return fetchCurrentUser(accessToken);
  }, [token, fetchCurrentUser]);

  const value = {
    user,
    token,
    isLoading,
    isAuthenticated,
    login,
    applyGuestSession,
    logout,
    register,
    refreshAccessToken,
    getAuthHeader,
    refreshUser,
    // Multi-account
    savedAccounts,
    activeAccountId,
    switchAccount,
    addAccount,
    signOutCurrent,
    refreshAccounts,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

export default AuthContext;
